﻿import {Base64} from "js-base64";
import * as CryptoJS from "crypto-js";
import {Ajax} from "./Ajax";
import Swal from "sweetalert2"
import * as Sentry from "@sentry/browser";
import i18next from "i18next";
import {concatWith} from "rxjs";

export class SigniflowOfficeApp {
    signiflowUrl: string = "";
    officeSdkReady: boolean = false;
    token: string = "";
    host: Office.HostType;
    uploadExtraData: any;
    _getSettings = async function (key): Promise<string> {
        if (document.hasStorageAccess) {
            return document.hasStorageAccess().then(function (hasStorageAccess) {
                if (!hasStorageAccess) {
                    return Promise.reject("No storage access");
                } else {
                    return document.requestStorageAccess().then(function () {
                        return Ajax.Execute({
                            EndPoint: '/Auth/GetCookieValues',
                            Type: "POST",
                            WithCredentials: true,
                            CorsEnable: false
                        }).then(function (response) {
                            try {
                                return response[key];
                            } catch (e) {
                                Sentry.addBreadcrumb({
                                    category: "GetSettings",
                                    message: "Error getting settings",
                                    data: {
                                        response: response,
                                        key: key
                                    },
                                    level: "info"
                                });
                                return null;
                            }
                        });
                    });
                }
            });
        } else {
            let jsonResponse = await Ajax.Execute({
                EndPoint: '/Auth/GetCookieValues',
                Type: "POST",
                WithCredentials: true,
                CorsEnable: false
            })
            try {
                return jsonResponse[key];
            } catch (e) {
                Sentry.addBreadcrumb({
                    category: "GetSettings",
                    message: "Error getting settings",
                    data: {
                        jsonResponse: jsonResponse,
                        key: key
                    },
                    level: "info"
                });
                return null;
            }
        }
    }
    _resetPage = function (success) {
    }

    async init(host: Office.HostType) {
        this.host = host;
    }

    async cookieExists(): Promise<boolean> {
        let loginServer: string;
        try {
            loginServer = await this._getSettings("SigniFlowUrl");
            this.userLog(`Checking if cookie exists: ${loginServer}`);
            if (this.isValidHttpUrl(loginServer)) {
                this.signiflowUrl = loginServer;
                return true;
            }
        } catch (e) {
            Sentry.addBreadcrumb({
                message: "Error getting cookie",
                category: "auth",
                data: {
                    loginServer: loginServer,
                    error: e
                }
            });
            return false;
        }
        return false;
    }

    async validateCookie(): Promise<boolean> {
        let loginServer: string;
        try {
            loginServer = await this._getSettings("SigniFlowUrl");
            this.userLog(`Logging into: ${loginServer}`);
            if (this.isValidHttpUrl(loginServer)) {
                if (await this.validateToken()) {
                    return true;
                }
            }
        } catch (e) {
            Sentry.addBreadcrumb({
                message: "Error getting cookie",
                category: "auth",
                data: {
                    loginServer: loginServer,
                    error: e
                }
            });
            Sentry.captureMessage('Error getting cookie', "info");
        }
        return false;
    }

    async logout() {
        let response = null;
        try {
            response = await Ajax.Execute({
                EndPoint: "/Auth/Logout",
                Type: "GET"
            });
        } catch (e) {
            Sentry.addBreadcrumb({
                message: "Logout",
                data: {
                    response: response
                }
            })
            Sentry.captureException(e);
        }
    }

    async login(username: string, password: string): Promise<boolean> {
        if (this.officeSdkReady) {
            let loginRequest = new LoginRequest(username, password)
            let response: boolean = false;
            let loginResponse: LoginResponse = await Ajax.Execute({
                EndPoint: "/API/SigniFlow/Login?sfUrl=" + encodeURIComponent(this.signiflowUrl),
                Data: loginRequest
            })
            if (loginResponse.ResultField == "Success") {
                response = true;
            }
            return response;
        } else {
            this.debugLog("Office SDK not ready")
        }
        return false
    }

    async getGravatar(): Promise<string> {
        try {
            let email = await this._getSettings("Email");
            this.userLog(`Getting Gravatar for: ${email}`);
            if (email) {
                let md5 = CryptoJS.MD5(email).toString();
                return `https://www.gravatar.com/avatar/${md5}?d=mp&s=40`;
            }
        } catch {
            // Gravatar is not available, don't care
        }

        return "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&s=40";
    }

    async getAvailableLoginOptions(): Promise<LoginOption[]> {
        let encrypted = this.encryptText(this.signiflowUrl);
        let getSettingsRequest: GetServerSettingsRequest = new GetServerSettingsRequest(new Token(encrypted));

        Sentry.addBreadcrumb({
            data: {
                getSettingsRequest: getSettingsRequest
            }
        })

        let settingsResponse: GetServerSettingsResponse = await Ajax.Execute({
            EndPoint: "/API/SigniFlow/GetServerSettings?sfUrl=" + encodeURIComponent(this.signiflowUrl),
            Type: "POST",
            Data: getSettingsRequest
        });

        let loginOptions: LoginOption[] = [];

        if (settingsResponse.ResultField === "Success") {
            for (const settingsField of settingsResponse.SettingsField) {
                if (settingsField.Key == "ADType") {
                    if (settingsField.Value == "AAD") {
                        loginOptions.push(new LoginOption("AAD", "AAD"));
                    }
                }

                if (settingsField.Key == "SamlProviders") {
                    let samlProviders = JSON.parse(settingsField.Value);

                    for (const samlProvider in samlProviders) {
                        loginOptions.push(new LoginOption(samlProviders[samlProvider], samlProvider));
                    }
                }
            }
        }

        return loginOptions;
    }

    async validateToken(): Promise<boolean> {
        if (this.signiflowUrl) {
            let data: TokenValidateResponse = await Ajax.Execute({
                EndPoint: "/API/SigniFlow/TokenValidate?sfUrl=" + encodeURIComponent(this.signiflowUrl)
            });
            return data.ResultField != "Token Expired";
        }
        return false;
    }

    async uploadDocument(fileName?: string): Promise<boolean> {
        switch (this.host) {
            case Office.HostType.Word:
                await Word.run(async (context) => {
                    Office.context.document.getFilePropertiesAsync(async (result) => {
                        // eslint-disable-next-line no-empty
                        if (result.status == Office.AsyncResultStatus.Succeeded) {
                            let docName = result.value.url;
                            if (docName != "") {
                                docName = docName.replace("\\", "/").split("/").pop();
                            }
                            if (docName.length < 1) {
                                await Swal.fire({
                                    icon: "error",
                                    showDenyButton: false,
                                    showCancelButton: false,
                                    showConfirmButton: false,
                                    titleText: i18next.t("warning.docnamemissing", "Document name not found"),
                                    text: i18next.t("text.pleasesavedoc", "Please save the document before uploading.")
                                })
                                this._resetPage(false);
                                return;
                            }
                            await this.sendFile(docName);
                        } else {
                            this.debugLog(`AsyncResultStatus ${result.status}`)
                            let eventId = Sentry.captureMessage(`Unexpected AsyncResultStatus: ${result.status}`)
                            await Swal.fire({
                                icon: "error",
                                showDenyButton: false,
                                showCancelButton: false,
                                showConfirmButton: false,
                                titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                                text: i18next.t("text.contactsupportevent", {eventId: eventId})
                            })
                            this._resetPage(false);
                        }
                    });

                    await context.sync();
                });
                return true;
            case Office.HostType.Excel:
                await Excel.run(async (context) => {
                    Office.context.document.getFilePropertiesAsync(async result => {
                        // eslint-disable-next-line no-empty
                        if (result.status == Office.AsyncResultStatus.Succeeded) {
                            let docName = result.value.url;
                            if (docName != "") {
                                docName = docName.replace("\\", "/").split("/").pop();
                            }
                            if (docName.length < 1) {
                                await Swal.fire({
                                    icon: "error",
                                    showDenyButton: false,
                                    showCancelButton: false,
                                    showConfirmButton: false,
                                    titleText: "Document name not found",
                                    text: "Please save the document before uploading."
                                })
                                this._resetPage(false);
                                return;
                            }
                            await this.sendFile(docName);
                        } else {
                            this.debugLog(`AsyncResultStatus ${result.status}`)
                            let eventId = Sentry.captureMessage(`Unexpected AsyncResultStatus: ${result.status}`)
                            await Swal.fire({
                                icon: "error",
                                showDenyButton: false,
                                showCancelButton: false,
                                showConfirmButton: false,
                                titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                                text: i18next.t("text.contactsupportevent", {eventId: eventId})
                            })
                            this._resetPage(false);
                        }
                    });

                    await context.sync();
                });
                return true;
            case Office.HostType.PowerPoint:
                await PowerPoint.run(async (context) => {
                    Office.context.document.getFilePropertiesAsync(async result => {
                        // eslint-disable-next-line no-empty
                        if (result.status == Office.AsyncResultStatus.Succeeded) {
                            let docName = result.value.url;
                            if (docName != "") {
                                docName = docName.replace("\\", "/").split("/").pop();
                            }
                            if (docName.length < 1) {
                                await Swal.fire({
                                    icon: "error",
                                    showDenyButton: false,
                                    showCancelButton: false,
                                    showConfirmButton: false,
                                    titleText: "Document name not found",
                                    text: "Please save the document before uploading."
                                })
                                this._resetPage(false);
                                return;
                            }
                            await this.sendFile(docName);
                        } else {
                            this.debugLog(`AsyncResultStatus ${result.status}`)
                            let eventId = Sentry.captureMessage(`Unexpected AsyncResultStatus: ${result.status}`)
                            await Swal.fire({
                                icon: "error",
                                showDenyButton: false,
                                showCancelButton: false,
                                showConfirmButton: false,
                                titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                                text: i18next.t("text.contactsupportevent", {eventId: eventId})
                            })
                            this._resetPage(false);
                        }
                    });

                    await context.sync();
                });
                return true;
            case Office.HostType.Outlook:
                let attachment: Office.AttachmentDetails;
                if (fileName != undefined) {
                    attachment = Office.context.mailbox.item.attachments.filter(value => !value.isInline && value.name == fileName)[0];
                } else {
                    attachment = Office.context.mailbox.item.attachments.filter(value => !value.isInline)[0];
                }
                if (attachment) {
                    Office.context.mailbox.item.getAttachmentContentAsync(attachment.id, {}, async asyncResult => {
                        if (asyncResult.status == Office.AsyncResultStatus.Succeeded) {
                            if (asyncResult.value.format == Office.MailboxEnums.AttachmentContentFormat.Base64) {
                                try {
                                    await this.sendOutlookFile(attachment.name, attachment.contentType, asyncResult.value.content);
                                } catch (e) {
                                    let eventId = Sentry.captureException(e)
                                    await Swal.fire({
                                        icon: "error",
                                        showDenyButton: false,
                                        showCancelButton: false,
                                        showConfirmButton: false,
                                        titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                                        text: i18next.t("text.contactsupportevent", {eventId: eventId})
                                    })
                                    this._resetPage(false);
                                }
                            } else {
                                this.debugLog(asyncResult.value.format)
                                let eventId = Sentry.captureMessage(`Unsupported attachment format: ${asyncResult.value.format}`)
                                await Swal.fire({
                                    icon: "error",
                                    showDenyButton: false,
                                    showCancelButton: false,
                                    showConfirmButton: false,
                                    titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                                    text: i18next.t("text.contactsupportevent", {eventId: eventId})
                                })
                                this._resetPage(false);
                            }
                        } else {
                            this.debugLog(`AsyncResultStatus ${asyncResult.status}`)
                            let eventId = Sentry.captureMessage(`Unexpected AsyncResultStatus: ${asyncResult.status}`)
                            await Swal.fire({
                                icon: "error",
                                showDenyButton: false,
                                showCancelButton: false,
                                showConfirmButton: false,
                                titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                                text: i18next.t("text.contactsupportevent", {eventId: eventId})
                            })
                            this._resetPage(false);
                        }
                    });
                }
                return true;
            case Office.HostType.OneNote:
            case Office.HostType.Project:
            case Office.HostType.Access:
            default:
                await Swal.fire({
                    icon: "error",
                    showDenyButton: false,
                    showCancelButton: false,
                    showConfirmButton: false,
                    titleText: "Unsupported Application",
                    text: "This Office application is not supported"
                })
                this._resetPage(false);
                return false;
        }

    }

    // Log to debug (remove for production)
    debugLog(message) {
        if (typeof message === "string") {
            Sentry.addBreadcrumb({
                category: "debug",
                message: message
            })
        } else {
            Sentry.addBreadcrumb({
                category: "debug",
                message: JSON.stringify(message)
            })
        }
    }

    // This log should be visible to the user
    userLog(message) {
        console.log(message);
    }

    // Get all of the content from a PowerPoint or Word document
    async sendFile(docName: string) {
        await Office.context.document.getFileAsync(Office.FileType.Pdf, {sliceSize: 64000}, async result => {
            if (result.status == Office.AsyncResultStatus.Succeeded && result.value.size > 0) {
                // Get the File object from the result.
                let myFile = result.value;
                let state = {
                    file: myFile,
                    counter: 0,
                    sliceCount: myFile.sliceCount,
                    docId: "",
                    fileName: docName,
                };

                this.debugLog("Getting file of " + myFile.size + " bytes");
                try {
                    await this.getSlice(state);
                } catch (e) {
                    Sentry.addBreadcrumb({
                        category: "upload",
                        data: {
                            error: e,
                            state: state,
                            result: result
                        },
                        level: "log"
                    });
                    let eventId = Sentry.captureException(e)
                    await Swal.fire({
                        icon: "error",
                        showDenyButton: false,
                        showCancelButton: false,
                        showConfirmButton: false,
                        titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                        text: i18next.t("text.contactsupportevent", {eventId: eventId})
                    })
                    this._resetPage(false);
                }
            } else {
                this.debugLog(result.status);
            }
        });
    }

    async sendOutlookFile(fileName: string, fileType: string, base64: string) {
        let fileTypes = {
            "application/pdf": 0,
            "application/vnd.ms-excel": 1,
            "text/plain": 2,
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": 3,
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": 4,
            "application/msword": 5,
            "application/xml": 6,
            "image/png": 7,
            "image/jpeg": 8,
            "image/gif": 9,
        }

        let fileTypeCode = fileTypes[fileType];
        let docId = "";

        let uploadRequest = new UploadRequest();
        uploadRequest.Base64Chunk = base64;
        uploadRequest.Extension = fileTypeCode;
        uploadRequest.DocumentID = docId;
        uploadRequest.DocumentName = fileName;
        uploadRequest.Token = new Token("");
        uploadRequest.IsCompleted = false;

        this.debugLog("Outlook file sending")

        let response: UploadResponse = await Ajax.Execute({
            EndPoint: "/API/SigniFlow/UploadPartialDocument?sfUrl=" + encodeURIComponent(this.signiflowUrl),
            Data: uploadRequest
        });

        this.debugLog(response.Result)

        if (response.Result == "Success") {
            docId = response.DocumentID;

            let finishRequest = new UploadRequest();
            finishRequest.Extension = fileTypeCode;
            finishRequest.DocumentID = docId;
            finishRequest.DocumentName = fileName;
            finishRequest.Base64Chunk = "";
            finishRequest.IsCompleted = true;
            finishRequest.Token = new Token("");
            let finishResponse: UploadResponse = await Ajax.Execute({
                EndPoint: "/API/SigniFlow/UploadPartialDocument?sfUrl=" + encodeURIComponent(this.signiflowUrl),
                Data: finishRequest
            });
            if (finishResponse.Result == "Success") {
                this.debugLog("File successfully sent")
                this._resetPage(true);
                let getCreateRequest: GetCreateWorkflowLinkRequest;
                let getCreateResponse: GetCreateWorkflowLinkResponse;
                try {
                    getCreateRequest = new GetCreateWorkflowLinkRequest();
                    getCreateRequest.DocIDField = docId;
                    getCreateRequest.TokenField = new Token("");
                    getCreateRequest.ReturnURLField = "";

                    getCreateResponse = await Ajax.Execute({
                        EndPoint: "/API/SigniFlow/GetCreateWorkflowLink?sfUrl=" + encodeURIComponent(this.signiflowUrl),
                        Data: getCreateRequest
                    });

                    this.debugLog(getCreateResponse);

                    if (getCreateResponse.Result == "Success") {
                        window.open(getCreateResponse.URL, '_blank')
                    } else {
                        window.open(`${this.signiflowUrl}/CreateFlow.aspx?DocID=${docId}`, '_blank');
                    }
                } catch (e) {
                    Sentry.addBreadcrumb({
                        message: "Unable to generate workflow link",
                        category: "upload",
                        data: {
                            error: e,
                            getCreateRequest: getCreateRequest,
                            getCreateResponse: getCreateResponse
                        }
                    });
                    Sentry.captureMessage(`Unable to generate workflow link on ${this.signiflowUrl}`, "log");
                    window.open(`${this.signiflowUrl}/CreateFlow.aspx?DocID=${docId}`, '_blank');
                }
            } else {
                var strippedRequest = finishRequest;
                strippedRequest.Base64Chunk = "stripped"
                Sentry.addBreadcrumb({
                    message: "Unable to send file",
                    category: "upload",
                    data: {
                        request: strippedRequest,
                        response: response
                    }
                });
                let eventId = Sentry.captureMessage(`Unable to send file on ${this.signiflowUrl}`, "log");
                await Swal.fire({
                    icon: "error",
                    showDenyButton: false,
                    showCancelButton: false,
                    showConfirmButton: false,
                    titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                    text: i18next.t("text.contactsupportevent", {eventId: eventId})
                })
                this._resetPage(false);
            }
        } else {
            var strippedRequest = uploadRequest;
            strippedRequest.Base64Chunk = "stripped"
            Sentry.addBreadcrumb({
                message: "Unable to send file",
                category: "upload",
                data: {
                    request: strippedRequest,
                    response: response
                }
            });
            let eventId = Sentry.captureMessage(`Unable to send file on ${this.signiflowUrl}`, "log");
            await Swal.fire({
                icon: "error",
                showDenyButton: false,
                showCancelButton: false,
                showConfirmButton: false,
                titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                text: i18next.t("text.contactsupportevent", {eventId: eventId})
            })
            this._resetPage(false);
        }
    }

    async getSlice(state) {
        await state.file.getSliceAsync(state.counter, async result => {
            try {
                if (result.status == Office.AsyncResultStatus.Succeeded) {
                    this.debugLog("Sending piece " + (state.counter + 1) + " of " + state.sliceCount);
                    await this.sendSlice(result.value, state);
                } else {
                    this.debugLog(result.status);
                }
            } catch (e) {
                this.debugLog(e);
                Sentry.addBreadcrumb({
                    message: "Unable to send slice",
                    category: "upload",
                    data: {
                        state: state,
                        result: result
                    }
                });
                let eventId = Sentry.captureException(e);
                await Swal.fire({
                    icon: "error",
                    showDenyButton: false,
                    showCancelButton: false,
                    showConfirmButton: false,
                    titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                    text: i18next.t("text.contactsupportevent", {eventId: eventId})
                })
                this._resetPage(false);
            }
        });
    }

    async sendSlice(slice, state) {
        let data = slice.data;

        // If the slice contains data, create an HTTP request.
        if (data) {
            let fileData = Base64.encode(data);

            let uploadRequest = new UploadRequest();
            uploadRequest.Base64Chunk = fileData;
            uploadRequest.Extension = 0; //pdf
            uploadRequest.DocumentID = state.docId;
            uploadRequest.DocumentName = state.fileName;
            uploadRequest.Token = new Token("");
            uploadRequest.IsCompleted = false;

            let response: UploadResponse = await Ajax.Execute({
                EndPoint: "/API/SigniFlow/UploadPartialDocument?sfUrl=" + encodeURIComponent(this.signiflowUrl),
                Data: uploadRequest
            });
            if (response.Result == "Success") {
                state.docId = response.DocumentID;

                this.debugLog("Sent " + slice.size + " bytes.");

                state.counter++;
                if (state.counter < state.sliceCount) {
                    await this.getSlice(state);
                } else {
                    await this.closeFile(state);
                }
            } else {
                var strippedRequest = uploadRequest;
                strippedRequest.Base64Chunk = "stripped"
                Sentry.addBreadcrumb({
                    message: "Unable to send file",
                    category: "upload",
                    data: {
                        request: strippedRequest,
                        response: response
                    }
                });
                let eventId = Sentry.captureMessage(`Unable to send file on ${this.signiflowUrl}`, "log");
                await Swal.fire({
                    icon: "error",
                    showDenyButton: false,
                    showCancelButton: false,
                    showConfirmButton: false,
                    titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                    text: i18next.t("text.contactsupportevent", {eventId: eventId})
                })
                await this.closeFile(state);
                this._resetPage(false);
            }

        }
    }

    async closeFile(state) {
        let uploadRequest = new UploadRequest();
        uploadRequest.Extension = 0; //pdf
        uploadRequest.DocumentID = state.docId;
        uploadRequest.DocumentName = state.fileName;
        uploadRequest.Base64Chunk = "";
        uploadRequest.IsCompleted = true;
        uploadRequest.Token = new Token("");

        let response: UploadResponse = await Ajax.Execute({
            EndPoint: "/API/SigniFlow/UploadPartialDocument?sfUrl=" + encodeURIComponent(this.signiflowUrl),
            Data: uploadRequest
        });
        if (response.Result == "Success") {
            this.debugLog("File successfully sent")
            this._resetPage(true);
            let getCreateRequest: GetCreateWorkflowLinkRequest;
            let getCreateResponse: GetCreateWorkflowLinkResponse
            try {
                getCreateRequest = new GetCreateWorkflowLinkRequest();
                getCreateRequest.DocIDField = state.docId;
                getCreateRequest.TokenField = new Token("");
                getCreateRequest.ReturnURLField = "";

                getCreateResponse = await Ajax.Execute({
                    EndPoint: "/API/SigniFlow/GetCreateWorkflowLink?sfUrl=" + encodeURIComponent(this.signiflowUrl),
                    Data: getCreateRequest
                });

                this.debugLog(getCreateResponse);

                if (getCreateResponse.Result == "Success") {
                    window.open(getCreateResponse.URL, '_blank')
                } else {
                    window.open(`${this.signiflowUrl}/CreateFlow.aspx?DocID=${state.docId}`, '_blank');
                }
            } catch (e) {
                {
                    Sentry.addBreadcrumb({
                        message: "Unable to generate workflow link",
                        category: "upload",
                        data: {
                            error: e,
                            getCreateRequest: getCreateRequest,
                            getCreateResponse: getCreateResponse
                        }
                    });
                    Sentry.captureMessage(`Unable to generate workflow link on ${this.signiflowUrl}`, "log");
                    window.open(`${this.signiflowUrl}/CreateFlow.aspx?DocID=${state.docId}`, '_blank');
                }
            }

            // Close the file when you're done with it.
            state.file.closeAsync(result => {
                // If the result returns as a success, the
                // file has been successfully closed.
                if (result.status == "succeeded") {
                    this.debugLog("File closed.");
                } else {
                    this.debugLog("File couldn't be closed.");
                }
            });
        } else {
            var strippedRequest = uploadRequest;
            strippedRequest.Base64Chunk = "stripped"
            Sentry.addBreadcrumb({
                message: "Unable to send file",
                category: "upload",
                data: {
                    request: strippedRequest,
                    response: response
                }
            });
            let eventId = Sentry.captureMessage(`Unable to send file on ${this.signiflowUrl}`, "log");
            await Swal.fire({
                icon: "error",
                showDenyButton: false,
                showCancelButton: false,
                showConfirmButton: false,
                titleText: i18next.t("title.somethingwrong", "Something went wrong"),
                text: i18next.t("text.contactsupportevent", {eventId: eventId})
            })
            await this.closeFile(state);
            this._resetPage(false);
        }
    }

    validURL(str: string): boolean {
        if (str) {
            let pattern = new RegExp(
                "^(https?:\\/\\/)?" + // protocol
                "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
                "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
                "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
                "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
                "(\\#[-a-z\\d_]*)?$", "i"
            ); // fragment locator
            return !!pattern.test(str);
        }
        return false;
    }

    encryptText(text: string) {
        let key = CryptoJS.enc.Latin1.parse("-x{w2o3aM?Qw?=j6");
        let encrypted = CryptoJS.AES.encrypt(text, key, {
            mode: CryptoJS.mode.CFB,
            padding: CryptoJS.pad.NoPadding,
            iv: key
        });
        return encrypted.toString();
    }

    isValidHttpUrl(testUrl: string): boolean {
        let url;

        try {
            url = new URL(testUrl);
        } catch (_) {
            return false;
        }

        return url.protocol === "http:" || url.protocol === "https:";
    }
}

class Serializable {
    fillFromJSON(json: string) {
        var jsonObj = JSON.parse(json);
        for (var propName in jsonObj) {
            this[propName] = jsonObj[propName]
        }
    }
}

export class Token extends Serializable {
    public TokenField: string;
    public TokenExpiryField: string;

    constructor(token: string) {
        super();
        this.TokenField = token;
        this.TokenExpiryField = `/Date(${Date.now()}+0000)/`;
    }
}

export class TokenValidateResponse extends Serializable {
    public ResultField: string;
    public TokenField: Token;
}

export class AzureBiscuit extends Serializable {
    public APIToken: string;
    public Email: string;

    constructor(email: string, token: string, server: string) {
        super();
        this.Email = email;
        this.APIToken = token;
    }
}

export class LoginRequest extends Serializable {
    public PasswordField: string;
    public UserNameField: string;

    constructor(email: string, password: string) {
        super();
        this.UserNameField = email;
        this.PasswordField = password;
    }
}

export class LoginResponse extends Serializable {
    public ResultField: string;
    public TokenField: Token;
}

export class UploadRequest extends Serializable {
    public DocumentID: string;
    public DocumentName: string;
    public Extension: number;
    public Base64Chunk: string;
    public IsCompleted: boolean;
    public Token: Token;
}

export class UploadResponse extends Serializable {
    public Result: string;
    public DocumentID: string;
}

export class GetServerSettingsRequest extends Serializable {
    public TokenField: Token;

    constructor(token: Token) {
        super();
        this.TokenField = token;
    }
}

export class GetServerSettingsResponse extends Serializable {
    public ResultField: string;
    public SettingsField: SettingsField[];
}

export class SettingsField extends Serializable {
    public Key: string;
    public Value: string;
}

export class UserInfo extends Serializable {
    guildName: string;
    licenseURL: string;
    licenseDescription: string;
}

export class GetCreateWorkflowLinkRequest extends Serializable {
    public DocIDField: string;
    public ReturnURLField: string;
    public TokenField: Token;
}

export class GetCreateWorkflowLinkResponse extends Serializable {
    public Result: string;
    public URL: string;
}

export class LoginOption {
    public OptionName: string;
    public OptionId: string;

    constructor(name: string, id: string) {
        this.OptionName = name;
        this.OptionId = id;
    }
}